<template>
  <div class="wg-order-flow">
    <div class="wg-order-flow__back-row">
      <router-link
        :to="{ name: 'OverviewWhiteGlove', params: { siteId: $route.params.siteId } }"
        class="wg-order-flow__back-link"
      >
        <i class="material-icons">arrow_back</i>
        <span>Back to {{ backLabel }}</span>
      </router-link>
      <white-glove-admin-mode-toggle />
    </div>

    <h1 class="wg-order-flow__title mainColor--text mb-4">
      Research new topics
    </h1>
    <p class="wg-order-flow__subtitle gray60Color--text mb-6">
      Add up to 100 topics or keywords (one per line). We'll pull the search metrics for you, then you can use AI clustering to label them as top-level or supporting, and turn them into a content order.
    </p>

    <div v-if="siteLoading" class="wg-order-flow__loading mainColor--text">
      Loading site…
    </div>
    <div v-else-if="siteLoadError" class="wg-order-flow__error mainColor--text">
      We couldn’t load this site. Try again or return to your projects.
    </div>
    <template v-else-if="site && site.id">
      <div class="wg-order-only__form-wrap white-common-box p-5">
        <form-section-white-glove
          :reload-user="reloadUser"
          :site="site"
          :campaign="domainLevelCampaign"
          :has-unlimited-access="hasUnlimitedAccess"
          :is-white-glove-user="isWhiteGloveUser"
          :is-mission-control-session="isMissionControlSession"
          :isContentOnlyView="true"
          submit-button-label="Research keywords"
          :allow-white-glove-submit="true"
          :after-success-navigate="afterKeywordInsightCreated"
          @domain-campaign-created="onDomainCampaignCreated"
        />
      </div>

      <template v-if="domainLevelCampaign">
        <hr class="wg-order-only__hr">

        <list-section
          ref="keywordInsightList"
          :reload-user="reloadUser"
          :site="site"
          :campaign="domainLevelCampaign"
          :has-unlimited-access="hasUnlimitedAccess"
          :is-white-glove-user="isWhiteGloveUser"
          :is-mission-control-session="isMissionControlSession"
          :isContentOnlyView="true"
          insight-detail-route-name="ContentOnlyKeywordInsightSingleWhiteGlove"
        />
      </template>
    </template>

    <async-tasks-popup
      v-if="siteId"
      :site-id="siteId"
      :campaign-id="domainLevelCampaign ? domainLevelCampaign.id : 0"
    />
  </div>
</template>

<script>
import AsyncTasksPopup from '@/components/AsyncTasksPopup.vue'
import WhiteGloveAdminModeToggle from '@/components/SitesSingleView/WhiteGlove/WhiteGloveAdminModeToggle.vue'
import FormSectionWhiteGlove from '@/components/SitesSingleView/WhiteGlove/FormSectionWhiteGlove.vue'
import ListSection from '@/components/Campaigns/keyword-research/list-view/ListSection.vue'
import {
  SUBSCRIPTIONS_WITH_UNLIMITED_ACCESS_TO_KEYWORD_RESEARCH_TOOLS,
  whiteGlovePlans,
} from '@/components/plans_constants'
import { getPureServicePkgName } from '@/utils'

export default {
  name: 'OrderContentOnlyWhiteGlove',
  components: {
    AsyncTasksPopup,
    WhiteGloveAdminModeToggle,
    FormSectionWhiteGlove,
    ListSection,
  },
  data () {
    return {
      siteLoading: true,
      siteLoadError: false,
      campaigns: [],
    }
  },
  computed: {
    siteId () {
      const id = parseInt(this.$route.params.siteId, 10)
      return Number.isFinite(id) ? id : null
    },
    site () {
      if (!this.siteId) return null
      return this.$store.getters.siteById(this.siteId) || null
    },
    backLabel () {
      return (this.site && this.site.name) ? this.site.name : 'site'
    },
    currentUser () {
      return this.$store.state.user
    },
    selectedSubAccountRelation () {
      return this.$store.state.selectedSubAccountRelation
    },
    isSpecialAdminUser () {
      return this.$store.getters.isSpecialAdminUser
    },
    isSpecialOwnerSelected () {
      return this.$store.getters.isSpecialOwnerSelected
    },
    domainLevelCampaigns () {
      return this.campaigns.filter((item) => item.isDomainLevelCampaign)
    },
    domainLevelCampaign () {
      return this.domainLevelCampaigns.length > 0 ? this.domainLevelCampaigns[0] : null
    },
    hasUnlimitedAccess () {
      if (this.selectedSubAccountRelation) {
        if (this.isSpecialOwnerSelected) {
          return true
        }
        if (SUBSCRIPTIONS_WITH_UNLIMITED_ACCESS_TO_KEYWORD_RESEARCH_TOOLS.map((x) => getPureServicePkgName(x)).includes(getPureServicePkgName(this.selectedSubAccountRelation.ownerAccountType))) {
          return true
        }
      } else {
        if (this.isSpecialAdminUser) {
          return true
        }
        if (this.currentUser && SUBSCRIPTIONS_WITH_UNLIMITED_ACCESS_TO_KEYWORD_RESEARCH_TOOLS.map((x) => getPureServicePkgName(x)).includes(getPureServicePkgName(this.currentUser.accountType))) {
          return true
        }
      }
      return false
    },
    isWhiteGloveUser () {
      if (!this.currentUser) return false
      const accountType = this.selectedSubAccountRelation
        ? this.selectedSubAccountRelation.ownerAccountType
        : this.currentUser.accountType
      return whiteGlovePlans.includes(getPureServicePkgName(accountType))
    },
    isMissionControlSession () {
      return Boolean(this.$store.state.selectedSubAccountRelation?.isMissionControlSubAccount)
    },
  },
  watch: {
    siteId: {
      handler () {
        this.ensureSiteLoaded()
      },
    },
  },
  mounted () {
    this.ensureSiteLoaded()
  },
  methods: {
    reloadUser () {
      return this.$store.dispatch('loadUser').then((response) => {
        this.$store.commit('setUser', response.data.user)
      })
    },
    afterKeywordInsightCreated (id) {
      if (id) {
        this.$router.push(`/sites/${this.siteId}/order-content-only/insight/${id}`)
      }
      this.$nextTick(() => {
        this.$refs.keywordInsightList?.loadReports({ silent: true })
      })
    },
    onDomainCampaignCreated (campaign) {
      const existing = this.campaigns.findIndex((c) => c.id === campaign.id)
      if (existing >= 0) {
        this.$set(this.campaigns, existing, campaign)
      } else {
        this.campaigns.push(campaign)
      }
    },
    ensureSiteLoaded () {
      if (!this.siteId) {
        this.siteLoading = false
        this.siteLoadError = true
        return Promise.resolve()
      }
      this.siteLoading = true
      this.siteLoadError = false
      return this.$store.dispatch('loadSite', this.siteId)
        .then((response) => {
          if (response.data && response.data.status === 'SUCCESS' && response.data.site) {
            this.$store.commit('setSite', response.data.site)
          } else {
            this.siteLoadError = true
          }
        })
        .catch(() => {
          this.siteLoadError = true
        })
        .finally(() => {
          this.siteLoading = false
          if (!this.siteLoadError) {
            this.loadCampaigns()
          }
        })
    },
    loadCampaigns () {
      return this.$store.dispatch('getCampaignsForSite', this.siteId)
        .then((response) => {
          if (response.data && response.data.status === 'SUCCESS') {
            this.campaigns = response.data.campaigns || []
          }
        })
    },
  },
}
</script>

<style lang="scss" scoped>
.wg-order-flow {
  padding: 20px 20px 0;

  &__back-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 16px;
  }

  &__title {
    font-size: 1.5rem;
    font-weight: 800;
    line-height: 1.25;
    margin: 12px 0 8px;
    letter-spacing: -0.02em;
  }

  &__subtitle {
    font-size: 0.9rem;
    line-height: 1.5;
    opacity: 0.9;
    margin: 0 0 20px;
    max-width: 580px;
  }

  &__loading,
  &__error {
    font-size: 0.85rem;
    margin: 16px 0;
  }

  &__back-link {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    color: var(--v-mainColor-base);
    font-size: 0.85rem;
    font-weight: 500;
    text-decoration: none;

    .material-icons {
      font-size: 18px;
    }
  }
}

.wg-order-only__form-wrap {
  max-width: 600px;
  width: 100%;
  overflow-x: initial;
}

.wg-order-only__hr {
  background-color: var(--v-gray12Color-base);
  height: 1px;
  border: none;
  margin: 2.5rem 0;
}
</style>
